import React, { useEffect, useMemo, useState } from "react";
import { Mail, MapPin, Phone, User, Copy, Check, CopyCheck } from "lucide-react";
import { buildBulkMailto, formatPhone } from "./utils";

function ContactRow({ icon: Icon, iconColor, label, value, href, onCopy, copied, copyTitle, testId }) {
  const display = (
    <div className="flex items-center gap-3 min-w-0 flex-1">
      <div className={`h-8 w-8 rounded-md flex items-center justify-center flex-shrink-0 ${iconColor}`}>
        <Icon className="h-3.5 w-3.5 text-white" strokeWidth={2.5} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">{label}</p>
        <p className="text-sm font-semibold text-zinc-950 truncate">{value}</p>
      </div>
    </div>
  );

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 bg-white p-3 hover:border-zinc-950 transition-colors" data-testid={testId}>
      {href ? (
        <a href={href} className="flex items-center gap-3 min-w-0 flex-1 group">
          {display}
        </a>
      ) : display}
      {onCopy && (
        <button
          type="button"
          onClick={onCopy}
          className="inline-flex items-center gap-1 text-[11px] font-bold text-zinc-500 hover:text-zinc-950 px-2 py-1 rounded-md hover:bg-zinc-50 flex-shrink-0"
          title={copyTitle || "Copy"}
          data-testid={`${testId}-copy`}
        >
          {copied ? <Check className="h-3 w-3 text-green-700" /> : <Copy className="h-3 w-3" />}
          <span className="hidden sm:inline">{copied ? "Copied" : (copyTitle || "Copy")}</span>
        </button>
      )}
    </div>
  );
}

export function PartiesTabs({ tabs, copy, copied }) {
  const [activeKey, setActiveKey] = useState(tabs[0]?.key || null);

  // Default to first tab whenever the tab list changes (e.g., navigating between leads).
  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.find((t) => t.key === activeKey)) setActiveKey(tabs[0].key);
  }, [tabs, activeKey]);

  const active = useMemo(() => tabs.find((t) => t.key === activeKey) || tabs[0], [tabs, activeKey]);
  if (!active) return null;

  const showSwitcher = tabs.length > 1;

  return (
    <div data-testid="parties-tabs">
      {showSwitcher && (
        <div className="border-b border-zinc-200 mb-4 -mx-1 overflow-x-auto" role="tablist">
          <div className="flex items-center gap-1 px-1">
            {tabs.map((t) => {
              const isActive = t.key === activeKey;
              return (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`parties-tab-${t.key}`}
                  onClick={() => setActiveKey(t.key)}
                  className={`relative px-4 py-2.5 text-sm whitespace-nowrap transition-colors ${
                    isActive
                      ? "font-bold text-zinc-950"
                      : "font-semibold text-zinc-500 hover:text-zinc-950"
                  }`}
                >
                  {t.label}
                  {isActive && (
                    <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-zinc-950 rounded-t" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid={`parties-tab-panel-${active.key}`}>
        {(active.phones?.length > 1 || active.emails?.length > 1) && (
          <div className="flex flex-wrap items-center gap-2 -mt-1 mb-1" data-testid={`parties-${active.key}-bulk-actions`}>
            {active.phones?.length > 1 && (
              <button
                type="button"
                data-testid={`parties-${active.key}-copy-all-phones`}
                onClick={() => {
                  const text = active.phones.map((p) => formatPhone(p) || p).join("\n");
                  copy(text, `${active.key}-all-phones`);
                }}
                className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold text-zinc-700 hover:border-zinc-950 hover:text-zinc-950 transition-colors"
              >
                {copied === `${active.key}-all-phones` ? <CopyCheck className="h-3.5 w-3.5 text-green-700" /> : <Copy className="h-3.5 w-3.5" />}
                {copied === `${active.key}-all-phones` ? "Copied" : `Copy all ${active.phones.length} phones`}
              </button>
            )}
            {active.emails?.length > 1 && (
              <>
                <button
                  type="button"
                  data-testid={`parties-${active.key}-copy-all-emails`}
                  onClick={() => {
                    const text = active.emails.join("\n");
                    copy(text, `${active.key}-all-emails`);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs font-bold text-zinc-700 hover:border-zinc-950 hover:text-zinc-950 transition-colors"
                >
                  {copied === `${active.key}-all-emails` ? <CopyCheck className="h-3.5 w-3.5 text-green-700" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied === `${active.key}-all-emails` ? "Copied" : `Copy all ${active.emails.length} emails`}
                </button>
                <a
                  href={buildBulkMailto(active.emails, "Get this lead")}
                  data-testid={`parties-${active.key}-send-email-all`}
                  className="inline-flex items-center gap-1.5 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white px-3 py-1.5 text-xs font-bold transition-colors"
                >
                  <Mail className="h-3.5 w-3.5" />
                  Send email to all {active.emails.length}
                </a>
              </>
            )}
          </div>
        )}
        {active.name && (
          <ContactRow
            icon={User}
            iconColor="bg-zinc-950"
            label={active.shortLabel || active.label}
            value={active.name}
            testId={`parties-${active.key}-name`}
          />
        )}
        {active.address && (
          <ContactRow
            icon={MapPin}
            iconColor="bg-indigo-700"
            label="Address"
            value={active.address}
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(active.address)}`}
            onCopy={() => copy(active.address, `${active.key}-address`)}
            copied={copied === `${active.key}-address`}
            copyTitle="Copy Address"
            testId={`parties-${active.key}-address`}
          />
        )}
        {active.phones?.map((p, i) => {
          const digits = String(p).replace(/\D/g, "");
          return (
            <ContactRow
              key={`p-${i}`}
              icon={Phone}
              iconColor="bg-green-700"
              label={`Phone ${i + 1}`}
              value={formatPhone(p) || p}
              href={`tel:${digits}`}
              onCopy={() => copy(digits, `${active.key}-phone-${i}`)}
              copied={copied === `${active.key}-phone-${i}`}
              copyTitle="Copy Number"
              testId={`parties-${active.key}-phone-${i}`}
            />
          );
        })}
        {active.emails?.map((e, i) => (
          <ContactRow
            key={`e-${i}`}
            icon={Mail}
            iconColor="bg-blue-700"
            label={`Email ${i + 1}`}
            value={e}
            href={`mailto:${e}`}
            onCopy={() => copy(e, `${active.key}-email-${i}`)}
            copied={copied === `${active.key}-email-${i}`}
            copyTitle="Copy Email"
            testId={`parties-${active.key}-email-${i}`}
          />
        ))}
        {!active.name && !active.address && !active.phones?.length && !active.emails?.length && (
          <p className="text-sm text-zinc-400 py-3">No contact details on file for this party.</p>
        )}
      </div>
    </div>
  );
}
